import React, { useState, useEffect, useCallback, useRef } from 'react';
import IdentityDashboard from '../components/ensapp/IdentityDashboard';
import { Link } from 'react-router-dom';

const POPUP_FEATURES = 'width=420,height=700,menubar=no,toolbar=no,location=no,status=no';
const ZPOST_ORIGIN = 'https://zpost.ai';

const PublicMintPage = () => {
  const [walletAddress, setWalletAddress] = useState(null);
  const [walletConnected, setWalletConnected] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const popupRef = useRef(null);
  const pendingRequests = useRef(new Map());

  // Listen for messages from wallet popup
  useEffect(() => {
    console.log('📻 PublicMintPage: message listener installed');

    const handleMessage = (event) => {
      console.log('📨 Raw message received:', { origin: event.origin, data: event.data });
      if (!event.origin.includes(ZPOST_ORIGIN)) {
        console.log('⚠️ Ignoring message from origin:', event.origin);
        return;
      }
      const data = event.data;
      if (!data || !data.source) {
        console.log('⚠️ Message missing source:', data);
        return;
      }
      console.log('✅ Valid message from source:', data.source, 'type:', data.type);

      if (data.type === 'ZWALLET_CONNECTED' || data.type === 'ZWALLET_READY') {
        if (data.address) {
          console.log('🔌 Setting wallet address:', data.address);
          setWalletAddress(data.address);
          setWalletConnected(true);
          setError('');
          if (popupRef.current && !popupRef.current.closed) {
            popupRef.current.focus();
          }
        }
      }

      if (data.type === 'ZWALLET_CREATED' && data.address) {
        console.log('🆕 Wallet created:', data.address);
        setWalletAddress(data.address);
        setWalletConnected(true);
        setError('');
      }

      if (data.type === 'TRANSACTION_RESULT' && data.messageId) {
        console.log('💰 Transaction result:', data);
        const pending = pendingRequests.current.get(data.messageId);
        if (pending) {
          pending.resolve(data.result);
          pendingRequests.current.delete(data.messageId);
        }
      }
      if (data.type === 'TRANSACTION_ERROR' && data.messageId) {
        console.log('❌ Transaction error:', data);
        const pending = pendingRequests.current.get(data.messageId);
        if (pending) {
          pending.reject(new Error(data.error || 'Transaction failed'));
          pendingRequests.current.delete(data.messageId);
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (popupRef.current && !popupRef.current.closed) {
        popupRef.current.close();
      }
    };
  }, []);

  const openWalletPopup = (mode = 'create') => {
    const url = `${ZPOST_ORIGIN}/wallet?mode=${mode}&standalone=true&ts=${Date.now()}`;
    try {
      if (popupRef.current && !popupRef.current.closed) {
        popupRef.current.close();
      }
      const popup = window.open(url, 'ZwalletPopup', POPUP_FEATURES);
      if (!popup) {
        setError('Popup blocked. Please allow popups for this site.');
        return;
      }
      popupRef.current = popup;
    } catch (err) {
      setError('Failed to open wallet popup.');
    }
  };

  const createWallet = () => openWalletPopup('create');
  const loginWallet = () => openWalletPopup('unlock');

  const disconnectWallet = () => {
    if (popupRef.current && !popupRef.current.closed) {
      popupRef.current.close();
    }
    setWalletAddress(null);
    setWalletConnected(false);
    setError('');
    popupRef.current = null;
  };

  const sendTransaction = useCallback(async (tx) => {
    if (!popupRef.current || popupRef.current.closed) {
      throw new Error('Wallet not connected. Please open the wallet first.');
    }

    const messageId = `tx-${Date.now()}-${Math.random().toString(36).substring(2)}`;
    const payload = {
      type: 'SIGN_TRANSACTION',
      source: 'public-mint',
      messageId,
      transaction: tx,
      from: walletAddress,
      timestamp: Date.now()
    };

    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        pendingRequests.current.delete(messageId);
        reject(new Error('Transaction timeout'));
      }, 60000);

      pendingRequests.current.set(messageId, { resolve, reject, timer });

      try {
        popupRef.current.postMessage(payload, ZPOST_ORIGIN);
      } catch (err) {
        clearTimeout(timer);
        pendingRequests.current.delete(messageId);
        reject(err);
      }
    });
  }, [walletAddress]);

  const awaitWalletResponse = useCallback(async (popup, payload, origin, timeout = 60000) => {
    return sendTransaction(payload.transaction);
  }, [sendTransaction]);

  const handleDomainMinted = (ensName) => {
    setSuccess(`✅ Domain ${ensName} minted successfully!`);
    setTimeout(() => {
      window.location.href = '/';
    }, 3000);
  };

  if (walletConnected && walletAddress) {
    return (
      <div className="public-mint-page">
        {/* Minimal header with address and disconnect */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '1rem 2rem',
          borderBottom: '1px solid var(--border)',
          background: 'var(--black)'
        }}>
          <Link to="/" style={{ textDecoration: 'none' }}>
            <span className="gold-logo" style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
              Zpost
            </span>
          </Link>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
            <div style={{
              background: 'rgba(255,215,0,0.1)',
              padding: '0.5rem 1rem',
              borderRadius: '20px',
              border: '1px solid var(--gold)',
              fontSize: '0.9rem',
              color: 'var(--gold)'
            }}>
              {walletAddress.slice(0, 6)}...{walletAddress.slice(-4)}
            </div>
            <button
              onClick={disconnectWallet}
              className="btn btn-dark-red"
              style={{ padding: '0.4rem 1rem', fontSize: '0.9rem' }}
            >
              Disconnect
            </button>
          </div>
        </div>

        <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '2rem 1rem' }}>
          <IdentityDashboard
            integrationEnabled={true}
            receivedAddress={walletAddress}
            onDomainMinted={handleDomainMinted}
            identityAuth={{ walletAddress, isAuthenticated: true, ensName: null }}
            logoutIdentity={disconnectWallet}
            sendTransactionToWallet={sendTransaction}
            awaitWalletResponse={awaitWalletResponse}
            walletPopupRef={popupRef}
            walletIsReady={true}
            zpostProvider={null}
            ensureSessionToken={null}
          />
          {success && (
            <div style={{ marginTop: '2rem', padding: '1rem', background: 'rgba(0,255,0,0.1)', border: '1px solid #0f0', borderRadius: '8px' }}>
              <p style={{ color: '#0f0', textAlign: 'center' }}>{success}</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="public-mint-page">
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '1rem 2rem',
        borderBottom: '1px solid var(--border)',
        background: 'var(--black)'
      }}>
        <Link to="/" style={{ textDecoration: 'none' }}>
          <span className="gold-logo" style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
            Zpost
          </span>
        </Link>
      </div>

      <div className="container" style={{ padding: '4rem 1rem', maxWidth: '600px', margin: '0 auto' }}>
        <div className="content-card" style={{ padding: '2rem', textAlign: 'center' }}>
          <h1 style={{ color: 'var(--gold)', marginBottom: '1rem' }}>Create Your .zpost.eth Identity</h1>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '2rem' }}>
            To mint a .zpost.eth domain, you need a Zwallet. Choose an option below.
          </p>

          {error && (
            <div style={{ marginBottom: '1rem', padding: '0.75rem', background: 'rgba(255,0,0,0.1)', border: '1px solid #f00', borderRadius: '4px' }}>
              <p style={{ color: '#f00', margin: 0 }}>{error}</p>
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginTop: '2rem' }}>
            <button
              onClick={createWallet}
              className="btn btn-gold"
              style={{ padding: '1rem', fontSize: '1.2rem' }}
            >
              🆕 Create New Zwallet
            </button>
            <button
              onClick={loginWallet}
              className="btn btn-dark-red"
              style={{ padding: '1rem', fontSize: '1.2rem' }}
            >
              🔓 Log in with Zwallet
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicMintPage;
